import { useState, useEffect, useCallback } from "react";
import { COMPANY_MANAGEMENT_SERVICE } from "src/utils/Api";
import __ from "src/utils/Translations";
import { mockData as companyMockData } from "src/Pages/Company/CompanyList";
import useHasPermission from "src/utils/hooks/security/useHasPermission";
import { companyCompanyPermissionRead } from "src/utils/RoleBasedSecurity/permissions";
import sendBatchRequests from "src/utils/Api/sendBatchRequests";
import { mappingAsOption } from "src/utils/Api/modifyResponse";

export default function useCompanies({
  asOption = false,
  filterField = null,
  filterValue = null,
  idAsIri = false,
  noFetching = false,
  withAdditionalMap = false,
  mappingType = null,
}) {
  const [data, setData] = useState([]);
  const [dataMap, setDataMap] = useState({});
  const hasPermission = useHasPermission(companyCompanyPermissionRead);
  const skipAction = !hasPermission || noFetching;

  const modifyResponse = useCallback(
    (resData) => {
      const result = resData;
      const filteredResult = [];
      const mappedResult = {};

      result.forEach((el) => {
        let res = el;

        if (idAsIri) {
          res.id = `${IRI_PREFIX}/${el.id}`;
        }
        if (asOption) {
          if (mappingType) {
            res = mappingAsOption()[mappingType](el);
          } else {
            res = {
              value: res.id,
              label: res.shortName,
              fullName: res.fullName,
            };
          }
        }

        if (withAdditionalMap) {
          mappedResult[el.id] = res;
        }

        filteredResult.push(res);
      });

      setDataMap((data) => ({ ...data, ...mappedResult }));
      return filteredResult;
    },
    [idAsIri, asOption, withAdditionalMap, mappingType, setDataMap]
  );

  const modifyAllResponses = useCallback(
    (resData) => {
      const result = resData.sort((a, b) => a.label.localeCompare(b.label));
      setData(result);
    },
    [setData]
  );

  useEffect(() => {
    const fetchData = async () =>
      sendBatchRequests({
        skipAction,
        filterField,
        filterValue,
        idAsIri,
        asOption,
        IRI_PREFIX,
        requestParams: {
          service: COMPANY_MANAGEMENT_SERVICE,
          path: asOption ? "/companies-names" : "/companies",
          method: "GET",
          mockData: companyMockData,
        },
        customModifyResponse: modifyResponse,
        customModifyAllResponses: modifyAllResponses,
      });

    fetchData();
  }, [
    filterField,
    filterValue,
    idAsIri,
    asOption,
    skipAction,
    modifyResponse,
    modifyAllResponses,
  ]);

  return withAdditionalMap ? [data, dataMap] : data;
}

export const IRI_PREFIX = "/api/company-management/v1/rest/companies";
